// frontend/src/services/llmService.js
import axios from 'axios';
import { API_URL } from '../config';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const llmService = {
  // Get all LLMs
  getAllLLMs: async () => {
    try {
      const response = await axios.get(`${API_URL}/api/llms`, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error fetching LLMs:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch LLMs');
    }
  },

  // Get LLM by ID
  getLLMById: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/llms/${id}`, getAuthHeaders());
      //console.log('response', response);
      return response.data;
    } catch (error) {
      console.error('Error fetching LLM:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch LLM');
    }
  },

  // Create new LLM
  createLLM: async (llmData) => {
    try {
      const response = await axios.post(`${API_URL}/api/llms`, llmData, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error creating LLM:', error);
      throw new Error(error.response?.data?.error || 'Failed to create LLM');
    }
  },

  // Update LLM
  updateLLM: async (id, llmData) => {
    try {
      const response = await axios.put(`${API_URL}/api/llms/${id}`, llmData, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error updating LLM:', error);
      throw new Error(error.response?.data?.error || 'Failed to update LLM');
    }
  },

  // Delete LLM
  deleteLLM: async (id) => {
    try {
      const response = await axios.delete(`${API_URL}/api/llms/${id}`, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error deleting LLM:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete LLM');
    }
  },

  // Update LLM status
  updateLLMStatus: async (id, status) => {
    try {
      const response = await axios.patch(`${API_URL}/api/llms/${id}/status`, { status }, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error updating LLM status:', error);
      throw new Error(error.response?.data?.error || 'Failed to update LLM status');
    }
  },

  // Get LLM statistics
  getLLMStats: async () => {
    try {
      const response = await axios.get(`${API_URL}/api/llms/stats`, getAuthHeaders());
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch LLM statistics');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching LLM statistics:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch LLM statistics');
    }
  },

  // Test LLM connection
  testConnection: async (llmData) => {
    try {
      const response = await axios.post(`${API_URL}/api/llms/test-connection`, llmData, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error testing LLM connection:', error);
      throw new Error(error.response?.data?.error || 'Failed to test LLM connection');
    }
  },

  // Export to CSV
  exportToCSV: async () => {
    try {
      const response = await axios.get(`${API_URL}/api/llms/export/csv`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `llms_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting LLMs to CSV:', error);
      throw new Error(error.response?.data?.error || 'Failed to export LLMs to CSV');
    }
  },

  // Export to JSON
  exportToJSON: async () => {
    try {
      const response = await axios.get(`${API_URL}/api/llms/export/json`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `llms_${new Date().toISOString()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting LLMs to JSON:', error);
      throw new Error(error.response?.data?.error || 'Failed to export LLMs to JSON');
    }
  },

  // Import LLMs
  importLLMs: async (data, mode = 'merge') => {
    try {
      const response = await axios.post(
        `${API_URL}/api/llms/import`,
        { data, mode },
        getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error importing LLMs:', error);
      throw new Error(error.response?.data?.error || 'Failed to import LLMs');
    }
  },

  // Download template
  downloadTemplate: () => {
    const template = [
      {
        name: "Example LLM",
        provider: "openai",
        modelType: "gpt-4",
        description: "An example LLM configuration",
        status: "active",
        apiBaseURL: "https://api.openai.com/v1",
        endpointPath: "/chat/completions",
        authentication: {
          type: "apiKey",
          headerName: "Authorization"
        },
        connection: {
          timeout: 30000,
          maxRetries: 3,
          retryDelay: 1000,
          verifySSL: true
        },
        features: ["chat", "completion"],
        capabilities: ["text generation", "code completion"],
        limitations: ["context window", "token limits"]
      }
    ];

    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'llm-template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
};

export default llmService;